import type { ICustomerRef } from '@team-milva/lab-models/customer'
import type { CustomerAccount } from '@team-milva/lab-models/customerAccount'
import type { InsuranceCustomerAccount } from '@team-milva/lab-models/insuranceCustomerAccount'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'

/**
 * The account a company customer's users, invites and private drop points belong to (MIL-402): a
 * business customer (`CustomerAccount`) or an insurance customer (`InsuranceCustomerAccount`).
 * A private customer has neither users of their own nor private drop points.
 */
export type TCompanyCustomerType = 'business' | 'insurance'

/** One string per owner, for grouping users/invites/drop points — an ID alone is not unique across the two collections. */
export function customerKey(customer: ICustomerRef): string {
  return `${customer.type}:${customer.id}`
}

export function isCompanyCustomer(
  customer: ICustomerRef
): customer is ICustomerRef & { type: TCompanyCustomerType } {
  return customer.type === 'business' || customer.type === 'insurance'
}

/**
 * Route names of the owner's Brugere / Drop points tabs and their sub-routes. The two full-screen
 * account views mount the same views under different routes, so the shared views navigate through
 * these rather than hard-coding the business names.
 */
export interface ICustomerOwnerRouteNames {
  users: string
  userDetails: string
  userRoles: string
  dropPoints: string
  privateDropPointInfo: string
}

/**
 * Everything the shared Brugere / Drop points views and the user modals need to know about the
 * account they act on, without knowing which kind of account it is. Built from the account model
 * by `businessCustomerOwner` / `insuranceCustomerOwner`.
 */
export interface ICustomerOwner {
  customer: ICustomerRef & { type: TCompanyCustomerType }
  name: string
  /** The user acting as the account's primary contact, or `null` while none is set — business and insurance alike. */
  primaryContactUserID: string | null
  /** The account's one-way welcome-email opt-in (MIL-397). */
  sendWelcomeEmails: boolean
  /** The route params that identify the account in its own routes. */
  routeParams: Record<string, string>
  routeNames: ICustomerOwnerRouteNames
}

export function businessCustomerOwner(
  customerAccount: CustomerAccount
): ICustomerOwner {
  return {
    customer: { type: 'business', id: customerAccount.id },
    name: customerAccount.name,
    primaryContactUserID: customerAccount.primaryContactUserID || null,
    // `?? false` because the field is absent on every customer account created before the
    // opt-in existed — which is the normal case, not an edge one.
    sendWelcomeEmails: customerAccount.options.sendWelcomeEmails ?? false,
    routeParams: { customerAccountID: customerAccount.id },
    routeNames: {
      users: 'CustomerEditUsers',
      userDetails: 'CustomerUserEditDetails',
      userRoles: 'CustomerUserEditRoles',
      dropPoints: 'CustomerEditDropPoints',
      privateDropPointInfo: 'CustomerEditPrivateDropPointInfo',
    },
  }
}

export function insuranceCustomerOwner(
  insuranceCustomerAccount: InsuranceCustomerAccount
): ICustomerOwner {
  return {
    customer: { type: 'insurance', id: insuranceCustomerAccount.id },
    name: insuranceCustomerAccount.name,
    primaryContactUserID: insuranceCustomerAccount.primaryContactUserID || null,
    sendWelcomeEmails: insuranceCustomerAccount.options.sendWelcomeEmails,
    routeParams: { insuranceCustomerAccountID: insuranceCustomerAccount.id },
    routeNames: {
      users: 'InsuranceCustomerAccountEditUsers',
      userDetails: 'InsuranceCustomerAccountUserEditDetails',
      userRoles: 'InsuranceCustomerAccountUserEditRoles',
      dropPoints: 'InsuranceCustomerAccountEditDropPoints',
      privateDropPointInfo: 'InsuranceCustomerAccountEditPrivateDropPointInfo',
    },
  }
}

/**
 * Whether the account's users get a welcome email when they are created (MIL-397), for either kind
 * of company customer. Reads the stores, so it stays reactive inside a computed.
 */
export function isWelcomeEmailEnabledForCustomer(customer: ICustomerRef): boolean {
  if (customer.type === 'business') {
    return useCustomerAccountStore().isWelcomeEmailEnabledFor(customer.id)
  }
  if (customer.type === 'insurance') {
    return (
      useInsuranceCustomerAccountStore().allInsuranceCustomerAccounts.find(
        (x) => x.id === customer.id
      )?.options.sendWelcomeEmails ?? false
    )
  }
  return false
}

/** Whether `route` is the owner's Brugere or Drop points tab, or one of their sub-routes. */
export function isCustomerOwnerTabRoute(
  route: { matched: { name?: unknown }[] },
  customerOwner: ICustomerOwner
): boolean {
  const { users, dropPoints } = customerOwner.routeNames
  return route.matched.some((x) => x.name === users || x.name === dropPoints)
}
